import * as React from 'react';

/** How long past `durationMs` the fallback timer waits before force-settling. */
export const SETTLE_TIMEOUT_MARGIN_MS = 150;

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface ITransitionStageDriverOptions {
  /** Whether a transition is staged right now; nothing runs while false. */
  isTransitioning: boolean;
  /**
   * Re-arms the flip and the settle timer whenever it changes — the stage, plus
   * anything else that restarts the CSS transition (a retargeted value).
   */
  stageKey: string;
  /** The CSS transition's duration. The fallback timer fires this plus a margin after each stage starts. */
  durationMs: number;
  /**
   * The CSS property whose `transitionend` settles a stage (e.g. `'width'`,
   * `'height'`). Other properties finishing on the same element — and any
   * event bubbling up from a child — are ignored, so a shorter child fade
   * cannot truncate the element's own transition.
   */
  transitionProperty: string;
  /** The double-rAF flip landed: move a `pre` stage to its active one. */
  onFrame: () => void;
  /** The transition finished, or the fallback timer fired. */
  onSettle: () => void;
}

/**
 * The stage engine every transition hook shares: the double-rAF flip that
 * gives the browser two distinct style states to transition between, the
 * settle timer that covers a throttled tab or a `transitionend` that never
 * fires, and the `transitionend` handler filtered to the element's own named
 * property. The reducers that decide what a stage *means* stay with each hook.
 *
 * `onFrame` and `onSettle` are read through refs, so callers can pass fresh
 * closures without re-arming the timer on every render.
 *
 * @param options.isTransitioning Whether a transition is staged right now; nothing runs while false.
 * @param options.stageKey Re-arms the flip and the settle timer whenever it changes.
 * @param options.durationMs The CSS transition's duration; the fallback timer fires this plus a margin.
 * @param options.transitionProperty The property whose `transitionend`, on the element itself, settles a stage.
 * @param options.onFrame Called when the double-rAF flip lands.
 * @param options.onSettle Called when the transition finishes or the fallback timer fires.
 * @returns The `transitionend` handler to attach to the transitioning element itself.
 */
export function useTransitionStageDriver({
  isTransitioning,
  stageKey,
  durationMs,
  transitionProperty,
  onFrame,
  onSettle,
}: ITransitionStageDriverOptions): React.TransitionEventHandler {
  const onFrameRef = React.useRef(onFrame);
  const onSettleRef = React.useRef(onSettle);
  onFrameRef.current = onFrame;
  onSettleRef.current = onSettle;

  React.useEffect(() => {
    if (!isTransitioning) {
      return;
    }

    // Double rAF: the first fires just before the paint that commits the
    // pre-stage styles, so the flip lands on the following frame and the
    // browser sees two distinct style states to transition between.
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => onFrameRef.current());
    });

    const settleTimer = setTimeout(() => onSettleRef.current(), durationMs + SETTLE_TIMEOUT_MARGIN_MS);

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) {
        cancelAnimationFrame(secondFrame);
      }
      clearTimeout(settleTimer);
    };
    // `stageKey` re-arms the timer per stage; the pre-stage's timer would
    // otherwise fire mid-transition after the rAF flip consumed no time.
  }, [isTransitioning, stageKey, durationMs]);

  return React.useCallback<React.TransitionEventHandler>(
    (e) => {
      if (e.target !== e.currentTarget || e.propertyName !== transitionProperty) {
        return;
      }
      onSettleRef.current();
    },
    [transitionProperty]
  );
}
